import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { Search, Loader2, BookOpen, Users as UsersIcon, GraduationCap, User as UserIcon } from "@/components/icons";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

type Role = "participant" | "advisor" | "admin";

interface BaseHit {
  id: string;
  title: string;
  subtitle?: string;
  programId?: string;
}

interface SearchResponse {
  programs: BaseHit[];
  cohorts: BaseHit[];
  teams: BaseHit[];
  users: BaseHit[];
}

interface FlatItem extends BaseHit {
  group: keyof SearchResponse;
  url: string;
}

const GROUP_LABELS: Record<keyof SearchResponse, string> = {
  programs: "Programs",
  cohorts: "Cohorts",
  teams: "Teams",
  users: "Users",
};

const GROUP_ICONS: Record<keyof SearchResponse, typeof BookOpen> = {
  programs: BookOpen,
  cohorts: GraduationCap,
  teams: UsersIcon,
  users: UserIcon,
};

function buildUrl(group: keyof SearchResponse, hit: BaseHit, role: Role): string {
  if (group === "programs") {
    return role === "participant" ? `/program/${hit.id}` : `/advisor/${hit.id}`;
  }
  if (group === "cohorts") {
    if (role === "participant") return `/program/${hit.programId}`;
    return `/advisor/${hit.programId}?cohortId=${hit.id}`;
  }
  if (group === "teams") {
    if (role === "participant") return `/program/${hit.programId}`;
    return `/advisor/${hit.programId}?teamId=${hit.id}`;
  }
  // users
  if (role === "admin") return `/admin?userId=${hit.id}`;
  return `/conversations?userId=${hit.id}`;
}

function flatten(data: SearchResponse | undefined, role: Role): FlatItem[] {
  if (!data) return [];
  const order: (keyof SearchResponse)[] = ["programs", "cohorts", "teams", "users"];
  const out: FlatItem[] = [];
  for (const g of order) {
    for (const hit of data[g] || []) {
      out.push({ ...hit, group: g, url: buildUrl(g, hit, role) });
    }
  }
  return out;
}

export function HeaderSearch() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const role = (user?.role as Role | undefined) ?? "participant";

  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const [debounced, setDebounced] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounce input
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value.trim()), 200);
    return () => clearTimeout(id);
  }, [value]);

  // Reset highlight when query changes
  useEffect(() => setActiveIndex(0), [debounced]);

  // Global shortcuts: `/` and Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const inField =
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.isContentEditable);
      if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
        return;
      }
      if (e.key === "/" && !inField) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Outside click closes
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const { data, isFetching } = useQuery<SearchResponse>({
    queryKey: ["/api/search", debounced],
    queryFn: async ({ signal }) => {
      const res = await fetch(`/api/search?q=${encodeURIComponent(debounced)}`, {
        credentials: "include",
        signal,
      });
      if (!res.ok) throw new Error("Search failed");
      return res.json();
    },
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });

  const items = flatten(data, role);
  const showDropdown = open && (debounced.length > 0 || isFetching);

  const navigate = (url: string) => {
    setOpen(false);
    setValue("");
    setDebounced("");
    // Wouter doesn't parse query strings — strip them so the route matches,
    // but include them in the URL we push so the page can read them.
    if (url.includes("?")) {
      // setLocation supports full path including search.
      setLocation(url);
    } else {
      setLocation(url);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!showDropdown || items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = items[activeIndex];
      if (it) navigate(it.url);
    }
  };

  // Close on route change
  useEffect(() => {
    const handler = () => setOpen(false);
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);

  let cursor = -1;
  const groupedRender = (Object.keys(GROUP_LABELS) as (keyof SearchResponse)[])
    .map((g) => {
      const groupItems = items.filter((i) => i.group === g);
      if (groupItems.length === 0) return null;
      const Icon = GROUP_ICONS[g];
      return (
        <div key={g} className="py-1" data-testid={`search-group-${g}`}>
          <div className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {GROUP_LABELS[g]}
          </div>
          {groupItems.map((it) => {
            cursor += 1;
            const isActive = cursor === activeIndex;
            const myIndex = cursor;
            return (
              <div
                key={`${g}-${it.id}`}
                id={`search-item-${myIndex}`}
                role="option"
                aria-selected={isActive}
                onMouseEnter={() => setActiveIndex(myIndex)}
                onMouseDown={(e) => {
                  // mousedown prevents input blur from closing before click fires
                  e.preventDefault();
                  navigate(it.url);
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-left text-sm rounded-md transition-colors cursor-pointer",
                  isActive ? "bg-accent text-accent-foreground" : "hover:bg-accent/60",
                )}
                data-testid={`search-result-${g}-${it.id}`}
              >
                <Icon className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{it.title}</div>
                  {it.subtitle && (
                    <div className="truncate text-xs text-muted-foreground">{it.subtitle}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );
    })
    .filter(Boolean);

  const dropdownId = "header-search-listbox";
  const activeItemId = showDropdown && items.length > 0 ? `search-item-${activeIndex}` : undefined;

  return (
    <div ref={containerRef} className="relative hidden sm:block">
      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
      <label htmlFor="header-search" className="sr-only">Search</label>
      <input
        ref={inputRef}
        id="header-search"
        type="text"
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={dropdownId}
        aria-activedescendant={activeItemId}
        aria-autocomplete="list"
        placeholder="Search..."
        autoComplete="off"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="h-9 pl-9 pr-12 rounded-full bg-secondary/50 border-none text-sm focus:ring-2 focus:ring-primary/20 w-64 transition-all outline-none"
        data-testid="input-header-search"
      />
      <kbd
        className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 hidden md:inline-flex items-center gap-0.5 rounded border border-border bg-background/80 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
        aria-hidden="true"
      >
        /
      </kbd>

      {showDropdown && (
        <div
          id={dropdownId}
          className="absolute right-0 mt-2 w-[24rem] max-w-[90vw] rounded-lg border border-border bg-popover text-popover-foreground shadow-lg z-50 overflow-hidden"
          role="listbox"
          aria-label="Search results"
          data-testid="search-dropdown"
        >
          {debounced.length < 2 ? (
            <div className="p-4 text-sm text-muted-foreground" data-testid="search-hint">
              Keep typing to search…
            </div>
          ) : isFetching && !data ? (
            <div className="p-4 flex items-center gap-2 text-sm text-muted-foreground" data-testid="search-loading">
              <Loader2 className="w-4 h-4 animate-spin" />
              Searching…
            </div>
          ) : items.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground" data-testid="search-empty">
              No results for “{debounced}”.
            </div>
          ) : (
            <div className="max-h-[28rem] overflow-y-auto py-1">{groupedRender}</div>
          )}
        </div>
      )}
    </div>
  );
}
